import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full mb-4">
              <FileText className="w-8 h-8 text-black" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Conditions Générales d'Utilisation
            </h1>
            <p className="text-gray-400">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 space-y-8 text-gray-300">
            
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Objet</h2>
              <p>
                Les présentes Conditions Générales d'Utilisation (ci-après "CGU") régissent l'utilisation du service 
                de réservation de transport avec chauffeur proposé par Luxury Transfer Service (ci-après "le Service").
              </p>
              <p className="mt-3">
                En créant un compte ou en effectuant une réservation, vous acceptez sans réserve les présentes CGU.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Définitions</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Client</strong> : Toute personne physique ou morale utilisant le Service</li>
                <li><strong>Réservation</strong> : Commande d'un service de transport via la plateforme</li>
                <li><strong>Chauffeur</strong> : Professionnel indépendant ou salarié effectuant la prestation</li>
                <li><strong>Plateforme</strong> : Site web et application mobile de Luxury Transfer Service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Inscription et Compte Client</h2>
              <div className="space-y-3">
                <p><strong>3.1 Création de compte</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>L'inscription est gratuite et facultative pour réserver</li>
                  <li>Vous devez fournir des informations exactes et à jour</li>
                  <li>Vous êtes responsable de la confidentialité de votre compte</li>
                  <li>L'authentification se fait par "Magic Link" sécurisé envoyé par email</li>
                </ul>
                
                <p className="mt-4"><strong>3.2 Programme de fidélité</strong></p>
                <p>Un compte vous permet de bénéficier du programme de fidélité : 10% de réduction tous les 5 trajets complétés.</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Réservations</h2>
              <div className="space-y-3">
                <p><strong>4.1 Processus de réservation</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Vous pouvez réserver avec ou sans compte client</li>
                  <li>Les réservations sont confirmées par email</li>
                  <li>Un numéro de réservation unique vous est attribué</li>
                </ul>

                <p className="mt-4"><strong>4.2 Types de services</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Transfert simple</strong> : Point A vers point B</li>
                  <li><strong>Mise à disposition horaire</strong> : Minimum 3 heures</li>
                  <li><strong>Service corporate</strong> : Contrat entreprise sur-mesure</li>
                </ul>

                <p className="mt-4"><strong>4.3 Tarification</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Les prix affichés sont TTC (TVA 21% incluse)</li>
                  <li>Le prix final est calculé selon : distance + durée + trafic en temps réel</li>
                  <li>Réduction aller-retour : -10% pour les transferts simples</li>
                  <li>Les tarifs peuvent être modifiés sans préavis (nouveau prix appliqué aux nouvelles réservations)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Annulation et Modification</h2>
              <div className="space-y-3">
                <p><strong>5.1 Annulation par le client</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Plus de 24h avant</strong> : Annulation gratuite</li>
                  <li><strong>Entre 24h et 12h</strong> : 50% du montant facturé</li>
                  <li><strong>Moins de 12h</strong> : 100% du montant facturé</li>
                  <li><strong>No-show</strong> : 100% du montant facturé</li>
                </ul>

                <p className="mt-4"><strong>5.2 Modification de réservation</strong></p>
                <p>Contactez-nous par email ou téléphone. Modifications gratuites si effectuées plus de 12h avant le départ.</p>

                <p className="mt-4"><strong>5.3 Annulation par le Service</strong></p>
                <p>
                  En cas d'annulation de notre part (force majeure, indisponibilité), vous serez remboursé intégralement 
                  ou nous vous proposerons une alternative équivalente.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Paiement</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Paiement sécurisé en ligne (carte bancaire, virement)</li>
                <li>Paiement en espèces ou par terminal au chauffeur (selon accord préalable)</li>
                <li>Facture envoyée par email après chaque prestation</li>
                <li>Délai de paiement pour entreprises : 30 jours sur accord</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Obligations du Client</h2>
              <p className="mb-3">Vous vous engagez à :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Être présent à l'heure et au lieu de prise en charge convenu</li>
                <li>Respecter le chauffeur et le véhicule</li>
                <li>Ne pas fumer dans le véhicule</li>
                <li>Ne pas transporter de matières dangereuses ou illégales</li>
                <li>Signaler tout problème ou retard dans les plus brefs délais</li>
                <li>Respecter le nombre de passagers maximum du véhicule choisi</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Responsabilité</h2>
              <div className="space-y-3">
                <p><strong>8.1 Responsabilité du Service</strong></p>
                <p>
                  Nous mettons tout en œuvre pour assurer un service de qualité. Notre responsabilité ne peut être engagée 
                  en cas de force majeure (grève, intempéries, accidents, etc.).
                </p>

                <p className="mt-4"><strong>8.2 Assurance</strong></p>
                <p>Tous nos véhicules sont assurés conformément à la législation belge. En cas d'accident, contactez immédiatement notre service.</p>

                <p className="mt-4"><strong>8.3 Objets oubliés</strong></p>
                <p>
                  Nous ne sommes pas responsables des objets oubliés dans les véhicules. 
                  Contactez-nous dans les 48h pour signalement. Conservation maximum 30 jours.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Données Personnelles</h2>
              <p>
                La collecte et le traitement de vos données personnelles sont régis par notre 
                <button 
                  onClick={() => navigate('/privacy-policy')}
                  className="text-amber-500 hover:text-amber-400 underline mx-1"
                >
                  Politique de Confidentialité
                </button>
                conforme au RGPD.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Propriété Intellectuelle</h2>
              <p>
                Tous les contenus de la plateforme (textes, images, logos, code source) sont protégés par le droit d'auteur. 
                Toute reproduction non autorisée est interdite.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Résiliation</h2>
              <p className="mb-3">Nous nous réservons le droit de :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Suspendre ou supprimer votre compte en cas de non-respect des CGU</li>
                <li>Refuser une réservation sans justification</li>
                <li>Modifier ou interrompre le Service avec préavis</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Modifications des CGU</h2>
              <p>
                Nous pouvons modifier les présentes CGU à tout moment. Les modifications prennent effet dès leur publication 
                sur la plateforme. Vous serez notifié par email des changements importants.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Droit Applicable et Juridiction</h2>
              <p>
                Les présentes CGU sont régies par le droit belge. En cas de litige, nous privilégions une résolution amiable. 
                À défaut, les tribunaux de Bruxelles seront seuls compétents.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">14. Service Client</h2>
              <p>Pour toute question ou réclamation :</p>
              <div className="mt-4 bg-gray-900/50 p-4 rounded-lg">
                <p><strong>Luxury Transfer Service</strong></p>
                <p>Email : <a href="mailto:contact@luxurytransferservices.be" className="text-amber-500 hover:text-amber-400">contact@luxurytransferservices.be</a></p>
                <p>Téléphone : +32 2 315 02 54</p>
                <p className="text-sm text-gray-400 mt-2">Disponible 24/7</p>
              </div>
            </section>

            <section className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
              <p className="text-sm text-amber-300">
                <strong>📌 Note importante :</strong> En utilisant notre service ou en créant un compte, 
                vous reconnaissez avoir lu, compris et accepté les présentes Conditions Générales d'Utilisation 
                ainsi que notre Politique de Confidentialité.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
